/**
 * Display colors / labels for Investment Performance Explorer.
 * Keep aligned with backend/services/investmentAssetClassColors.js and migration seed.
 */

export const ASSET_CLASS_COLOR = {
  US_LARGE_CAP: '#5B5AC7',
  US_MID_CAP: '#3F8CFF',
  US_SMALL_CAP: '#52C7D9',
  DEVELOPED_INTL: '#F5B544',
  EMERGING: '#F47C2C',
  US_BONDS: '#82D889',
  INTL_BONDS: '#4EA58D',
  REAL_ESTATE: '#B566CC',
  COMMODITIES: '#A67C52',
  CASH: '#F39AC7',
  BLEND_60_40: '#CFCFCF',
  OTHER_UNMAPPED: '#DDDDDD',
};

export const BOND_COLOR = {
  AGG: '#4E79A7',
  SHY: '#59A14F',
  IEF: '#76B7B2',
  TLT: '#9C755F',
  TIP: '#EDC948',
  HYG: '#F28E2B',
  LQD: '#B07AA1',
  BNDX: '#499894',
  EMB: '#E15759',
  BIL: '#BAB0AC',
};

export const SECTOR_COLOR = {
  XLK: '#4E79A7',
  XLV: '#59A14F',
  XLF: '#B07AA1',
  XLY: '#F28E2B',
  XLP: '#EDC948',
  XLI: '#76B7B2',
  XLE: '#E15759',
  XLU: '#FFBE0B',
  XLB: '#9C755F',
  XLRE: '#AF7AA1',
  XLC: '#17A2B8',
};

export function colorForAsset(asset) {
  if (!asset) return ASSET_CLASS_COLOR.OTHER_UNMAPPED;
  if (asset.asset_group === 'bond') {
    return BOND_COLOR[asset.ticker] || ASSET_CLASS_COLOR.OTHER_UNMAPPED;
  }
  if (asset.asset_group === 'sector') {
    return SECTOR_COLOR[asset.ticker] || ASSET_CLASS_COLOR.OTHER_UNMAPPED;
  }
  return ASSET_CLASS_COLOR[asset.asset_class] || ASSET_CLASS_COLOR.OTHER_UNMAPPED;
}

/** @deprecated Prefer colorForAsset */
export function colorForAssetClass(assetClassKey) {
  return ASSET_CLASS_COLOR[assetClassKey] || ASSET_CLASS_COLOR.OTHER_UNMAPPED;
}

/** @param {number|null|undefined} decimalReturn e.g. 0.1234 → "+12.3%" */
export function formatReturnPct(decimalReturn, digits = 1) {
  if (decimalReturn === null || decimalReturn === undefined || Number.isNaN(Number(decimalReturn))) {
    return '—';
  }
  const pct = Number(decimalReturn) * 100;
  const sign = pct > 0 ? '+' : '';
  return `${sign}${pct.toFixed(digits)}%`;
}

export const EXPLORER_VIEWS = [
  {
    id: 'asset_classes',
    label: 'Asset Classes',
    assetGroup: 'asset_class',
    endpoint: '/api/public/investment-performance/asset-classes',
    quiltTitle: 'Asset class calendar-year returns',
    trailingTitle: 'Trailing returns',
    nameColumn: 'Asset class',
    enabled: true,
  },
  {
    id: 'sectors',
    label: 'Stock Sectors',
    assetGroup: 'sector',
    endpoint: '/api/public/investment-performance/sectors',
    quiltTitle: 'Stock sector calendar-year returns',
    trailingTitle: 'Sector trailing returns',
    nameColumn: 'Sector',
    enabled: true,
  },
  {
    id: 'bonds',
    label: 'Bond Categories',
    assetGroup: 'bond',
    endpoint: '/api/public/investment-performance/bonds',
    quiltTitle: 'Bond category calendar-year returns',
    trailingTitle: 'Bond trailing returns',
    nameColumn: 'Bond category',
    enabled: true,
  },
  {
    id: 'crypto',
    label: 'Cryptocurrency',
    assetGroup: 'crypto',
    endpoint: '/api/public/investment-performance/crypto',
    quiltTitle: 'Cryptocurrency Market Explorer',
    trailingTitle: 'Top cryptocurrencies by market capitalization',
    nameColumn: 'Cryptocurrency',
    enabled: true,
    kind: 'crypto',
  },
];

export function formatUsdCompact(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  const n = Number(value);
  if (n >= 1e12) return `$${(n / 1e12).toFixed(2)}T`;
  if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (n >= 1) return `$${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  return `$${n.toPrecision(3)}`;
}
